import useAuth from '@app/auth/use-auth';
import { FilterOptions } from '@app/components/data-table/hooks/use-server-filter-options';
import { PickerPeriod } from '@app/components/multi-period-picker';
import { pcfRoutes } from '@app/pcf/pcf-routes';
import { purchasedGoodsRoutes } from '@app/purchased-goods/purchased-goods-routes';
import { FoodFilterColumn, FoodFilterOption } from '@app/sold-goods/filters';
import {
  FoodOperationActivitiesList,
  SoldGoodModelledAsEnum,
} from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import TextLink from '@shared/components/content/text-link';
import Unit from '@shared/components/content/unit';
import { ActivityCell } from '@shared/components/data-table/cells/activity-cell';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import { TextCell } from '@shared/components/data-table/cells/text-cell';
import RatingCloud from '@shared/components/ratings/rating-cloud';
import { SimpleTooltip } from '@shared/components/simple-tooltip';
import { formatSmallNumber } from '@shared/lib/utils';
import { ColumnDef } from '@tanstack/react-table';
import { generatePath } from 'react-router-dom';

export function useSoldFoodAndDrinkTableColumns({
  filters,
  timePeriods,
}: {
  filters: FilterOptions<FoodFilterOption[]>;
  timePeriods: PickerPeriod[];
}): ColumnDef<FoodOperationActivitiesList>[] {
  const { user } = useAuth();
  const hasPurchasedGoodsAccess = user?.features.scope3;

  return [
    {
      id: 'uuid',
      accessorKey: 'uuid',
      enableHiding: true,
    },

    {
      id: 'remoteId',
      accessorKey: 'remoteId',
      header: ({ column }) => <HeaderCell column={column} name="Your ID" />,
      cell: ({ getValue }) => {
        return <Text>{getValue<string>()}</Text>;
      },
    },
    {
      id: 'food',
      accessorKey: 'food',
      accessorFn: (row) => row.soldGoodInfo,
      header: ({ column }) => <HeaderCell column={column} name="Name" />,
      cell: ({ row, getValue }) => {
        const remoteId = row.original.remoteId;
        const food = getValue<FoodOperationActivitiesList['soldGoodInfo']>();
        return (
          <ActivityCell
            className="w-full min-w-52"
            name={food.name}
            remoteId={remoteId!}
          />
        );
      },
      enableColumnFilter: true,
      meta: {
        label: 'Search item',
        variant: 'text',
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: 'matchedFood',
      accessorKey: 'matchedFood',
      accessorFn: (row) => row.soldGoodInfo,
      enableSorting: false,
      header: ({ column }) => (
        <HeaderCell column={column} name="Matched food" />
      ),
      cell: ({ getValue, row }) => {
        const modelledAs = row.original.modelledAs;
        const soldGood =
          getValue<FoodOperationActivitiesList['soldGoodInfo']>();

        if (modelledAs === SoldGoodModelledAsEnum.product) {
          return (
            <SimpleTooltip
              trigger={
                <span className="flex max-w-80 items-center gap-1">
                  <Icons.salad className="size-4 shrink-0 text-gray-500" />
                  <TextLink
                    className="truncate"
                    to={generatePath(pcfRoutes.PRODUCT_DETAIL, {
                      product_id: soldGood.productUuid,
                    })}
                  >
                    {soldGood.productName}
                  </TextLink>
                </span>
              }
              content={soldGood.productName}
            />
          );
        }

        if (modelledAs === SoldGoodModelledAsEnum.purchased_good) {
          return (
            <span className="flex max-w-80 items-center gap-1">
              <Icons.shoppingCart className="size-4 shrink-0 text-gray-500" />
              {hasPurchasedGoodsAccess && soldGood.purchasedGoodActivityUuid ? (
                <TextLink
                  className="truncate"
                  to={generatePath(
                    purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK_ITEM,
                    {
                      item_uuid: soldGood.purchasedGoodActivityUuid!,
                    }
                  )}
                >
                  {soldGood.purchasedGoodName}
                </TextLink>
              ) : (
                <Text className="truncate" includeHover>
                  {soldGood.purchasedGoodName}
                </Text>
              )}
            </span>
          );
        }

        if (modelledAs === SoldGoodModelledAsEnum.basic_food) {
          return (
            <Text className="max-w-80 truncate" includeHover>
              {soldGood.basicFoodName}
            </Text>
          );
        }

        if (modelledAs === SoldGoodModelledAsEnum.modelled_food) {
          return (
            <span className="flex max-w-80 items-center gap-1 truncate text-nowrap">
              <SimpleTooltip
                trigger={
                  <Icons.chefHat className="size-4 shrink-0 text-gray-500" />
                }
                content="This food item has been modelled using a custom recipe"
              />
              <Text includeHover>{soldGood.name}</Text>
            </span>
          );
        }

        if (modelledAs === SoldGoodModelledAsEnum.external_emission_factor) {
          return (
            <span className="flex max-w-80 items-center gap-1 truncate text-nowrap">
              <Icons.arrowRightCircle className="size-4 shrink-0 text-gray-500" />
              <Text className="text-gray-600" includeHover>
                External product assessment
              </Text>
            </span>
          );
        }

        const name = soldGood.name;
        return (
          <Text className="max-w-80 truncate" includeHover>
            {name}
          </Text>
        );
      },
      enableColumnFilter: true,
    },
    {
      accessorKey: 'emissionFactor',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Emission Factor"
          numberColumn
          unit={<Unit variant="kgCO2e/kg" />}
        />
      ),
      cell: ({ getValue, row }) => {
        const emissionFactor = getValue<number>();
        const emissionsCategory = row.original.emissionsCategory;
        return (
          <span className="flex items-center justify-end gap-2">
            <TextCell text={formatSmallNumber(emissionFactor)} type="number" />
            <RatingCloud className="w-10" rating={emissionsCategory} />
          </span>
        );
      },
    },
    {
      accessorKey: 'quantity',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Quantity"
          numberColumn
          unit={<Unit variant="kg" />}
        />
      ),
      cell: ({ getValue }) => {
        const quantity = getValue<number>();
        return <NumberCell number={quantity} />;
      },
    },
    {
      accessorKey: 'emissions',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Emissions"
          numberColumn
          unit={<Unit variant="tCO2e" />}
        />
      ),
      cell: ({ getValue }) => {
        const emissions = getValue<number>();
        return <NumberCell number={emissions} />;
      },
    },
    {
      id: FoodFilterColumn.Site,
      accessorKey: FoodFilterColumn.Site,
      enableColumnFilter: true,
      meta: {
        label: 'Site',
        variant: 'multiSelect',
        options: filters.sites,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: FoodFilterColumn.Customer,
      accessorKey: FoodFilterColumn.Customer,
      enableColumnFilter: true,
      meta: {
        label: 'Customer',
        variant: 'multiSelect',
        options: filters?.customers,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: FoodFilterColumn.Period,
      accessorKey: FoodFilterColumn.Period,
      meta: {
        label: 'Time period',
        variant: 'periodPicker',
        periods: timePeriods,
        sorting: { ascLabel: 'Oldest', descLabel: 'Newest' },
      },
      enableColumnFilter: true,
    },
    {
      id: FoodFilterColumn.ActivitiesImport,
      accessorKey: FoodFilterColumn.ActivitiesImport,
      enableColumnFilter: true,
      meta: {
        label: 'Import',
        variant: 'multiSelect',
        options: filters.activitiesImport,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: FoodFilterColumn.Tag,
      accessorKey: FoodFilterColumn.Tag,
      enableColumnFilter: true,
      meta: {
        label: 'Tags',
        variant: 'multiSelect',
        options: filters.tags,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
  ];
}
